import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, AlertTriangle, TrendingUp, Users, Calendar, CreditCard } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

export default function FinanceMetrics({ deals, contacts }) {
  // Total Receivables
  const totalReceivables = deals.reduce((sum, deal) => {
    if (deal.stage === 'closed_won') {
      return sum + ((deal.total_value || 0) - (deal.total_paid || 0));
    }
    return sum;
  }, 0);

  // Total Revenue Collected
  const totalCollected = deals.reduce((sum, deal) => sum + (deal.total_paid || 0), 0);

  // Delinquent Amounts
  const now = new Date();
  const delinquentDeals = deals.filter(deal => {
    if (deal.stage !== 'closed_won' || !deal.payment_plan) return false;
    
    return deal.payment_plan.some(installment => {
      const dueDate = new Date(installment.due_date);
      const isPastDue = dueDate < now;
      const isNotPaid = installment.status !== 'paid';
      return isPastDue && isNotPaid;
    });
  });

  const totalDelinquent = delinquentDeals.reduce((sum, deal) => {
    const delinquentAmount = (deal.payment_plan || []).reduce((installmentSum, installment) => {
      const dueDate = new Date(installment.due_date);
      if (dueDate < now && installment.status !== 'paid') {
        return installmentSum + (installment.amount - (installment.amount_paid || 0));
      }
      return installmentSum;
    }, 0);
    return sum + delinquentAmount;
  }, 0);

  // Delinquency by Customer
  const delinquencyByCustomer = delinquentDeals.map(deal => {
    const contact = contacts.find(c => c.id === deal.contact_id);
    const contactName = contact 
      ? `${contact.first_name} ${contact.last_name}`
      : 'Unknown Contact';

    const delinquentAmount = (deal.payment_plan || []).reduce((sum, installment) => {
      const dueDate = new Date(installment.due_date);
      if (dueDate < now && installment.status !== 'paid') {
        return sum + (installment.amount - (installment.amount_paid || 0));
      }
      return sum;
    }, 0);

    const overdueInstallments = (deal.payment_plan || []).filter(installment => {
      const dueDate = new Date(installment.due_date);
      return dueDate < now && installment.status !== 'paid';
    }).length;

    return {
      contactName,
      dealName: deal.name || deal.deal_name,
      amount: delinquentAmount,
      overdueCount: overdueInstallments,
      contact
    };
  }).sort((a, b) => b.amount - a.amount).slice(0, 10);

  // Payment Status Distribution
  const paymentStatuses = {
    paid_in_full: deals.filter(d => d.stage === 'closed_won' && d.total_paid >= d.total_value).length,
    in_progress: deals.filter(d => d.stage === 'closed_won' && d.total_paid > 0 && d.total_paid < d.total_value).length,
    not_started: deals.filter(d => d.stage === 'closed_won' && (d.total_paid || 0) === 0).length,
    overdue: delinquentDeals.length
  };

  // Upcoming Payments (next 30 days)
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(now.getDate() + 30);

  const upcomingPayments = [];
  deals.forEach(deal => {
    if (deal.stage !== 'closed_won' || !deal.payment_plan) return;
    
    deal.payment_plan.forEach(installment => {
      const dueDate = new Date(installment.due_date);
      if (dueDate >= now && dueDate <= thirtyDaysFromNow && installment.status !== 'paid') {
        const contact = contacts.find(c => c.id === deal.contact_id);
        upcomingPayments.push({
          contactName: contact ? `${contact.first_name} ${contact.last_name}` : 'Unknown',
          dealName: deal.name || deal.deal_name,
          amount: installment.amount - (installment.amount_paid || 0),
          dueDate: installment.due_date,
          installmentNumber: installment.installment_number
        });
      }
    });
  });

  upcomingPayments.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  const topUpcomingPayments = upcomingPayments.slice(0, 5);

  // Collection Rate
  const totalBilled = deals.reduce((sum, deal) => {
    if (deal.stage === 'closed_won') {
      return sum + (deal.total_value || 0);
    }
    return sum;
  }, 0);

  const collectionRate = totalBilled > 0 ? ((totalCollected / totalBilled) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-border bg-card hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Total Receivables</p>
              <DollarSign className="w-5 h-5 text-primary" />
            </div>
            <p className="text-3xl font-bold text-foreground">
              ${totalReceivables.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-muted-foreground mt-2">Outstanding balance</p>
          </CardContent>
        </Card>

        <Card className="border-border bg-card hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Total Collected</p>
              <TrendingUp className="w-5 h-5 text-primary" />
            </div>
            <p className="text-3xl font-bold text-foreground">
              ${totalCollected.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-muted-foreground mt-2">{collectionRate}% collection rate</p>
          </CardContent>
        </Card>

        <Card className="border-destructive/20 bg-destructive/5 hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-destructive font-semibold">Delinquent Amount</p>
              <AlertTriangle className="w-5 h-5 text-destructive" />
            </div>
            <p className="text-3xl font-bold text-destructive">
              ${totalDelinquent.toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-destructive/80 mt-2">{delinquentDeals.length} overdue accounts</p>
          </CardContent>
        </Card>

        <Card className="border-border bg-card hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Upcoming (30 days)</p>
              <Calendar className="w-5 h-5 text-primary" />
            </div>
            <p className="text-3xl font-bold text-foreground">
              ${upcomingPayments.reduce((sum, p) => sum + p.amount, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-muted-foreground mt-2">{upcomingPayments.length} payments due</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Delinquency by Customer */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-rose-600" />
              Top Delinquent Accounts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {delinquencyByCustomer.length > 0 ? (
              <div className="space-y-3">
                {delinquencyByCustomer.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-rose-50 rounded-lg border border-rose-100">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{item.contactName}</p>
                      <p className="text-sm text-slate-600">{item.dealName}</p>
                      <p className="text-xs text-rose-600 mt-1">
                        {item.overdueCount} overdue {item.overdueCount === 1 ? 'payment' : 'payments'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-rose-700">
                        ${item.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-8">Sem contas em atraso</p>
            )}
          </CardContent>
        </Card>

        {/* Payment Status Distribution */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Distribuição de Status de Pagamento
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Pago Integralmente</span>
                <span className="text-sm font-bold text-emerald-700">{paymentStatuses.paid_in_full}</span>
              </div>
              <Progress 
                value={deals.length > 0 ? (paymentStatuses.paid_in_full / deals.length) * 100 : 0} 
                className="h-2 [&>div]:bg-emerald-600" 
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Em Andamento</span>
                <span className="text-sm font-bold text-blue-700">{paymentStatuses.in_progress}</span>
              </div>
              <Progress 
                value={deals.length > 0 ? (paymentStatuses.in_progress / deals.length) * 100 : 0} 
                className="h-2 [&>div]:bg-blue-600" 
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Não Iniciado</span>
                <span className="text-sm font-bold text-amber-700">{paymentStatuses.not_started}</span>
              </div>
              <Progress 
                value={deals.length > 0 ? (paymentStatuses.not_started / deals.length) * 100 : 0} 
                className="h-2 [&>div]:bg-amber-600" 
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Em Atraso</span>
                <span className="text-sm font-bold text-rose-700">{paymentStatuses.overdue}</span>
              </div>
              <Progress 
                value={deals.length > 0 ? (paymentStatuses.overdue / deals.length) * 100 : 0} 
                className="h-2 [&>div]:bg-rose-600" 
              />
            </div>

            <div className="pt-4 border-t border-slate-200">
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-slate-900">Taxa de Recebimento</span>
                <span className="text-2xl font-bold text-emerald-600">{collectionRate}%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Payments */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-600" />
            Próximos Pagamentos (Próximos 30 dias)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {topUpcomingPayments.length > 0 ? (
            <div className="space-y-3">
              {topUpcomingPayments.map((payment, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">{payment.contactName}</p>
                    <p className="text-sm text-slate-600">{payment.dealName} - Installment #{payment.installmentNumber}</p>
                    <p className="text-xs text-purple-600 mt-1">
                      Due: {new Date(payment.dueDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-purple-700">
                      ${payment.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-8">No upcoming payments in the next 30 days</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}